import {cdnImage} from '@/util/cdnImage';
import React from 'react';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import {MUIContainer} from '@/components/MUI-Components';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {CENTRALIZED_CRYPTO_EXCHANGE_META_DATA} from '@/util/metaData/crypto';

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        cceBannerBottomData,
        cceDecodingData,
        cceServicesData,
        keyBenefitCCEData,
        cceIndustryData,
        debutCxMPCCheckPoints,
        debutCxMPCData,
        ndaxMPCCheckPoints,
        ndaxMPCData,
        metaTradeData,
        metaTradeCheckPoints,
        cceProcess,
        ourMakeCCEChoice,
        makeOurCCEContent,
        centralizedCryptoExchangeFAQ,
        centCryptoExchangeFeatureData,
        centralizedCryptoBannerPoints,
        centralizedDevelopmentSolution,
        CentralizedExchangeTechnologyData
    } = await import('@/util/contents/centralized-crypto-exchange-development');

    const {p2pCarsoulData} = await import('@/util/contents/p2p-data');

    return {
        props: {
            cceBannerBottomData,
            cceDecodingData,
            cceServicesData,
            keyBenefitCCEData,
            cceIndustryData,
            debutCxMPCCheckPoints,
            debutCxMPCData,
            ndaxMPCCheckPoints,
            ndaxMPCData,
            metaTradeData,
            metaTradeCheckPoints,
            cceProcess,
            ourMakeCCEChoice,
            makeOurCCEContent,
            centralizedCryptoExchangeFAQ,
            centCryptoExchangeFeatureData,
            centralizedCryptoBannerPoints,
            centralizedDevelopmentSolution,
            CentralizedExchangeTechnologyData,
            p2pCarsoulData
        }
    };
};

const Banner = dynamic(() => import('@/components/Ui-components/Banner'));
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
const Decoding = dynamic(() => import('@/components/Ui-components/Decoding'));
const HelperSection = dynamic(() => import('@/components/Ui-components/helper-section'));
const OurServices = dynamic(() => import('@/components/Ui-components/our-services'));
const DynamicScreens = dynamic(() => import('@/components/Ui-components/Dynamic-screens'));
const MobileProcess = dynamic(() => import('@/components/Ui-components/MobileProcess'));
const LetsDiscuss = dynamic(() => import('@/components/Ui-components/Lets-discuss'));
const OurMakeChoice = dynamic(() => import('@/components/Ui-components/OurMakeChoice'));
const Questions = dynamic(() => import('@/components/Ui-components/questions'));
const ServiceTab = dynamic(() => import('@/components/Ui-components/ServiceTab'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
import AggregateScript from '@/components/Common/Aggregate-script';
import {CDN_URL, SITE_URL} from '@/constants/urls';
const ndax = cdnImage('defi/NDAX.webp');
const debutCx = cdnImage('Centralized-crypto-exchnage/DebutCX.webp');
const metaTrade = cdnImage('decentralised/MetaTrader.webp');
const cceDevProcess = cdnImage('Centralized-crypto-exchnage/Centralized-Exchange-Approach.svg');
const centralizedCryptoBusiness = cdnImage('Centralized-crypto-exchnage/Centralized-Crypto-Exchange-overview.webp');
const LatestCTA = dynamic(() => import('@/components/Ui-components/LatestCta'));
const TokenizeHelp = dynamic(() => import('@/components/Ui-components/tokenize-help'));
const CardTechnology = dynamic(() => import('@/components/Ui-components/card-technology'));
const Platforms = dynamic(() => import('@/components/Ui-components/platforms'));

// Migrated from globals.scss `.centralized-crypto-exchange-main-parent .defi-banner-bottom-wrapper`
const cceBannerBottomWrapperSx = {padding: '20px 0 !important'} as const;

// Migrated from globals.scss `.centralized-decoding-wrapper` (+ nested `img`; base rule
// plus the `@media (min-width: 1441px)` tier). Sole consumer (matchCount: 2, base + the
// 1441px tier) — this page's own Grid item is the only element carrying this className.
const centralizedDecodingWrapperSx = {
    '& img': {
        maxWidth: 'unset !important'
    },
    '@media (min-width: 1441px)': {
        '& img': {
            maxWidth: '400px !important'
        }
    }
} as const;

const CentralizedCryptoExchange = (props: any) => {
    const {
        openForm,
        cceBannerBottomData,
        cceDecodingData,
        cceServicesData,
        keyBenefitCCEData,
        cceIndustryData,
        debutCxMPCCheckPoints,
        debutCxMPCData,
        ndaxMPCCheckPoints,
        ndaxMPCData,
        metaTradeData,
        metaTradeCheckPoints,
        cceProcess,
        ourMakeCCEChoice,
        makeOurCCEContent,
        centralizedCryptoExchangeFAQ,
        centCryptoExchangeFeatureData,
        centralizedCryptoBannerPoints,
        centralizedDevelopmentSolution,
        CentralizedExchangeTechnologyData,
        p2pCarsoulData
    } = props;
    return (
        <Box>
            <PageHead meta={CENTRALIZED_CRYPTO_EXCHANGE_META_DATA} url="/centralized-crypto-exchange-development" />
            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="Centralized Crypto Exchange Development Company"
                subheading="We create next-generation centralized exchanges engineered for speed, security, and scalability. Our solutions combine intuitive trading workflows, automated operations, and compliance-ready architecture to help you launch a platform that attracts traders, builds trust, and generates sustainable revenue in the global digital asset market."
                button="Book A Call With Experts"
                image={cdnImage('Centralized-crypto-exchnage/Centralized-crypto-exchange-development.webp')}
                bannerClass="justify-center centralized-crypto-exchange-banner"
                // bannerBackground="centralized-exchange-development"
                alt="Centralized Crypto Exchange Development Company"
                DataPoints={centralizedCryptoBannerPoints}
            />
            <Box className="sub-banner-wrapper defi-banner-bottom-wrapper" sx={cceBannerBottomWrapperSx}>
                <MUIContainer>
                    <Grid container spacing={2} alignItems="center" className="white-label-bottom-container">
                        <Grid item md={2} lg={1.7} sm="auto" xs={12}>
                            <Typography className="banner-bottom-heading">Key Partners</Typography>
                        </Grid>
                        {cceBannerBottomData?.map((data: any, index: number) => (
                            <Grid item md={2} lg={1.7} sm="auto" xs={6} className="sub-banner-images" key={index}>
                                <Box className="images-banner-bottom">
                                    <DisplayImage src={data.src} alt={data.alt} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MUIContainer>
            </Box>
            <Grid
                item
                xs={12}
                className="bottom-margin top-space decording-main-wrapper centralized-decoding-wrapper mobile-top-padding mobile-bottom-margin"
                sx={centralizedDecodingWrapperSx}
            >
                <MUIContainer>
                    <Decoding
                        imageSrc={centralizedCryptoBusiness}
                        alt="Centralized Crypto Exchange Development: Secure and Scalable Solutions for the Digital Economy"
                        heading="Centralized Crypto Exchange Development: Secure and Scalable Solutions for the Digital Economy"
                        subHeading={cceDecodingData}
                        isSocialMediaBtn={true}
                    />
                </MUIContainer>
            </Grid>

            <Grid item xs={12} className="helper-section">
                <Box className="helper_section_parent bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper solutions">
                            <Typography className="spectrum-heading color-black our-service-main-heading text-align-left" component="h2">
                                Our End-to-End Centralized Crypto Exchange Development Services
                            </Typography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line divider-line-left" />
                            </Box>
                            <Box className="sub-heading-box" pb={4}>
                                <Typography className="spectrum-sub-heading color-black spacing text-align-left">
                                    We provide an extensive range of centralized exchange development services, from building custom
                                    solutions to offering white label crypto exchange software, all designed to meet diverse market needs.
                                    Our services includes-
                                </Typography>
                            </Box>
                        </Box>
                        <Grid container spacing={2}>
                            <HelperSection data={cceServicesData} className="home-helper-card" />
                        </Grid>
                    </MUIContainer>
                </Box>
            </Grid>
            <Box className=" top-space mobile-top-padding">
                <LatestCTA
                    heading="Looking for a High-Liquidity Exchange Solution?"
                    subheading="Ensure seamless trading with real-time liquidity integration, low slippage, powerful market-making tools, and fast order book updates for optimal trading experience."
                    buttonText="Talk to Our Experts Now"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid
                item
                xs={12}
                className="top-space bottom-spacing helper-section mobile-top-padding blue-service-container-wrapper mobile-bottom-padding"
            >
                <MUIContainer>
                    <ServiceTab
                        heading="Our Feature-Rich Centralized Crypto Exchange Platform"
                        subheading="We offer a combination of fundamental and advanced features tailored for your cryptocurrency exchange development. Our development team expertly transforms complex challenges into straightforward, seamless solutions."
                        technology={centCryptoExchangeFeatureData}
                        className="blue-service-tab-card"
                    />
                </MUIContainer>
            </Grid>

            <Grid item xs={12} className="helper-section">
                <Box className="helper_section_parent bottom-spacing top-space mobile-top-padding">
                    <Grid container spacing={2} className="our-service-defi-wrapper">
                        <OurServices
                            ourServices={keyBenefitCCEData}
                            className="service-shadow-border-box transformative-benefit-card"
                            containerClass="card-heading-spacing"
                            heading="Key Benefits of Our Centralized Cryptocurrency Exchange Development Solutions"
                            subheading="Our centralized cryptocurrency exchange development solutions provide secure, scalable, and user-friendly platforms designed to streamline cryptocurrency trading. With intuitive interfaces, seamless functionality, and advanced security, our exchanges offer high liquidity, real-time data, and diverse investment opportunities. We ensure comprehensive compliance with regulations, providing a reliable solution for global trading needs."
                        />
                    </Grid>
                </Box>
            </Grid>
            <Box className="top-space mobile-top-padding">
                <LatestCTA
                    heading="Worried About Scalability as Your Exchange Grows?"
                    subheading="Design a flexible and scalable platform that supports high-volume trading, ensuring smooth expansion and consistent performance across regions."
                    buttonText="Share Your Project Needs"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>

            <Box className="bottom-spacing mobile-bottom-padding top-space mobile-top-padding">
                <MUIContainer>
                    <TokenizeHelp
                        heading="Centralized Cryptocurrency Exchange Development Solutions: Industries Revolutionizing with Digital Finance"
                        subheading="As a top provider of centralized cryptocurrency exchange development services in the UK, we deliver tailored solutions that help businesses optimize their operations and leverage the benefits of digital finance. Our secure and scalable platforms are designed to address the unique challenges of each sector, ensuring seamless crypto trading experiences and high levels of user satisfaction."
                        contentData={cceIndustryData}
                    />
                </MUIContainer>
            </Box>
            <hr className="hr-line zero-margin" />

            <Grid
                item
                xs={12}
                className="dynmaic-white-label-wrapper top-space bottom-margin dynamic-bep20-wrapper mobile-top-padding mobile-bottom-margin"
            >
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black text-align-left" component="h2">
                            Our Centralized Exchange Development Projects in Focus
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </Box>
                    </Box>
                    <DynamicScreens
                        imageSrc={debutCx}
                        heading="A Centralized Solution for Secure, Seamless Crypto Trading"
                        subPoints={debutCxMPCData}
                        reverseOrder={true}
                        alt="DebutDX"
                        checkItems={debutCxMPCCheckPoints}
                        openForm={openForm}
                        imageClass="dynamic-exchange-image"
                        buttonLabel="Get a Custom Quote"
                        buttonClass="nft-button"
                    />
                    <DynamicScreens
                        imageSrc={ndax}
                        heading="A Centralized Exchange Platform for Automated Crypto Trading"
                        subPoints={ndaxMPCData}
                        reverseOrder={false}
                        alt="NDAX Wallet"
                        checkItems={ndaxMPCCheckPoints}
                        openForm={openForm}
                        imageClass="dynamic-exchange-image"
                        buttonLabel="Get a Custom Quote"
                        buttonClass="helper-card-button"
                    />
                    <DynamicScreens
                        imageSrc={metaTrade}
                        heading="A Centralized Forex Trading Platform for Optimal Performance"
                        subPoints={metaTradeData}
                        reverseOrder={true}
                        alt="MetaTrader 4 Platform"
                        checkItems={metaTradeCheckPoints}
                        openForm={openForm}
                        buttonLabel="Get a Custom Quote"
                        buttonClass="nft-button"
                    />
                </MUIContainer>
            </Grid>
            <Box className="mobile-bottom-padding bottom-spacing">
                <LatestCTA
                    heading="Seeking a Crypto Exchange Tailored for Institutional Clients?"
                    subheading="Develop a high-performance exchange with advanced features, security, and liquidity integrations, built to meet the needs of institutions and professional traders."
                    buttonText="Get Expert’s Advice  Now!"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>

            <Grid item xs={12} className="blockchain-revolution-service bottom-spacing  mobile-bottom-padding">
                <MUIContainer>
                    <Platforms
                        data={centralizedDevelopmentSolution}
                        heading="Add-on Development Services: Enhancing Your Centralized Exchange Solution"
                        subheading="Elevate your CEX platform with our specialized development services. These features, integrated into your exchange, will set your platform apart in the ever-competitive cryptocurrency market."
                        containerClass="blockchain-card-container"
                        className="defi-feature-platform-service"
                    />
                </MUIContainer>
            </Grid>
            <Grid
                item
                xs={12}
                className="bottom-spacing generative-tech-wrapper mobile-bottom-padding top-space mobile-top-padding helper_section_parent"
            >
                <CardTechnology
                    heading="Centralized Crypto Exchange Development: Tech Stack We Use to Build High-Performance Platforms"
                    subheading="We employ a robust and scalable tech stack to design centralized cryptocurrency exchange platforms. By integrating cutting-edge technologies, we ensure secure, efficient, and compliance-ready solutions that deliver exceptional user experiences."
                    data={CentralizedExchangeTechnologyData}
                />
            </Grid>
            <Box className=" top-space mobile-top-padding">
                <LatestCTA
                    heading="Looking for a Seamless Multi-Asset Exchange Solution?"
                    subheading="Offer diverse trading opportunities by integrating a multi-asset exchange that supports digital currencies, tokens, and more with easy asset management."
                    buttonText="Request a Call With Experts"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Box className="bottom-spacing top-space mobile-top-padding process-mobile-bottom-padding">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black text-align-left" component="h2">
                            Development Approach of Our Centralized Exchange
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </Box>
                        <Box className="sub-heading-box" pb={4}>
                            <Typography className="spectrum-sub-heading color-black spacing text-align-left" component="p">
                                We follow industry-leading practices with a tailored approach, ensuring the perfect fit for your centralized
                                exchange project. Our meticulous process and commitment to excellence guarantee a solution that aligns with
                                your unique needs and goals.
                            </Typography>
                        </Box>
                    </Box>
                    <Box textAlign="center">
                        <DisplayImage
                            src={cceDevProcess}
                            alt="Development Approach of Our Centralized Exchange"
                            className="approach-main-img approach-web-img"
                        />
                    </Box>
                    <Box className="approach-main-img approach-mobile-img">
                        <MobileProcess mobileProcess={cceProcess} />
                    </Box>
                </MUIContainer>
            </Box>
            <OurMakeChoice
                heading="Why Choose"
                subheading="Debut Infotech as Your Centralized Exchange Development Partner?"
                body={ourMakeCCEChoice}
                contentData={makeOurCCEContent}
            />
            <Grid item xs={12} className="bottom-spacing">
                <LetsDiscuss
                    heading="Ready to Develop a Tailored Crypto Exchange for Your Business?"
                    subheading="Build a custom, secure exchange solution with advanced features and integrations designed to meet the unique needs of your enterprise."
                    btnLabel="Get Free Consultation Now!"
                    openForm={openForm}
                    className="white-label-sprill"
                />
            </Grid>
            <Grid item xs={12} className="question-parent top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={centralizedCryptoExchangeFAQ} />
                </MUIContainer>
            </Grid>
            <Grid item xs={12}>
                <Box className="bg-blue-background top-space mobile-top-padding">
                    <Testimonials heading="Our Clients Describe Us Best" clientData={p2pCarsoulData} />
                </Box>
            </Grid>
            <script
                id="centrealized-crypto-exchange-development"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'What is a centralized exchange?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'A centralized exchange (CEX) is a trading platform managed by a central authority, facilitating secure buying, selling, and trading of digital assets. It provides users with an easy-to-use platform and centralized control for efficient fund management and transaction handling.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How do I start a project with Debut Infotech?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'To start a project, fill out our inquiry form. Our team will get in touch to understand your requirements, provide insights, and guide you through building your centralized exchange platform.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How much does it cost to develop a centralized crypto exchange with Debut Infotech?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'The centralized crypto exchange cost varies depending on features, customization, and complexity. We offer flexible packages to suit diverse needs and provide a detailed estimate after initial discussions.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How long does it take to develop a centralized exchange?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Development timelines depend on complexity, customization, and regulatory requirements. Typically, it takes 4 to 6 months to complete, including design, development, testing, and deployment phases.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What security measures do you employ in exchange development?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: "We implement security measures like two-factor authentication (2FA), multi-signature wallets, encryption, DDoS protection, cold storage, and regular audits to safeguard users' assets and data, ensuring industry-standard security."
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can Debut Infotech assist me in defining my exchange’s features?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, our experts work closely with you to understand your vision and recommend essential features of ceentralized exchange based on market trends, user needs, and compliance to create a successful exchange.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Are your exchanges compatible with regulatory requirements?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, we ensure your exchange complies with global and regional standards, such as KYC and AML regulations. We guarantee adherence to the necessary frameworks for legal operations across jurisdictions.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can Debut Infotech customize exchange features to suit specific requirements?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, we offer customized solutions to meet your specific enterprise needs. Whether integrating unique trading pairs, security features, or a tailored user interface, we align our development with your business goals.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How do centralized exchanges provide liquidity?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Centralized exchanges ensure liquidity through a large user base, market makers, and institutional investors, providing sufficient volume and enabling smooth trading with minimal slippage.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What support services do you provide post-development?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'We offer comprehensive post-development support, including technical maintenance, feature upgrades, security monitoring, and bug fixes. Our dedicated support team ensures the smooth functioning of your exchange.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: "What differentiates Debut Infotech's centralized exchange solutions?",
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Our approach combines advanced technologies, personalized consultation, rigorous security, and deep industry expertise. Debut Infotech’s tailored solutions and dedicated client support help your exchange stand out in the competitive crypto market.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Will I have full control over the exchange development process?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, we ensure transparency in the development process, involving you in key stages—from planning to testing—so you have full visibility and influence over the project.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How do you ensure liquidity and market stability?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'We ensure liquidity by integrating market-making solutions and establishing partnerships with liquidity providers and institutional investors, guaranteeing consistent market stability for users.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can I launch my centralized exchange in multiple regions with regulatory differences?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, our solutions are designed for multi-region launches, accommodating the regulatory needs of different jurisdictions. We ensure compliance, making it feasible to operate globally.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What kind of documentation do you provide post-development?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'We provide detailed documentation, including user manuals and technical specifications. Debut Infotech also offers comprehensive training sessions to ensure you are fully equipped to operate and maintain the platform.'
                                }
                            }
                        ]
                    })
                }}
            />
            <AggregateScript
                name="Centralized Crypto Exchange Development"
                image={`${CDN_URL}?url=%2Fimages%2FCentralized-crypto-exchnage%2FCentralized-crypto-exchange-development.webp&w=750&q=75`}
                description="Centralized Crypto Exchange Development provides a secure, user-friendly platform for trading digital assets. Our tailored solutions include robust security, high liquidity, and efficient transaction processing, ensuring a seamless experience for users and administrators alike."
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.7',
                    bestRating: '5',
                    worstRating: '2',
                    ratingCount: '399'
                }}
                offers={{
                    '@type': 'AggregateOffer',
                    url: `${SITE_URL}/centralized-crypto-exchange-development`,
                    priceCurrency: 'USD',
                    lowPrice: '50000',
                    highPrice: '160000',
                    offerCount: '7'
                }}
            />
        </Box>
    );
};

export default CentralizedCryptoExchange;
CentralizedCryptoExchange.Layout = 'Main';
